import React from 'react';

import { Card, Typography } from '@mui/material';
import { BookOpenIcon } from 'src/components/icons/BookOpenIcon';
import { CrossIcon } from 'src/components/icons/CrossIcon';
import { IntegrationBlocksIcon } from 'src/components/icons/IntegrationBlocksIcon';
import { RevisionHistoryIcon } from 'src/components/icons/RevisionHistoryIcon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  card: {
    '&:hover': {
      cursor: 'pointer',
    },
    border: theme.appBorders.outline,
    borderRadius: 12,
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
  },
  cardsContainer: {
    alignItems: 'stretch',
    display: 'grid',
    gap: theme.spacing(2),
    gridAutoRows: '1fr',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    width: '100%',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxWidth: 900,
    padding: theme.spacing(0),
    width: '100%',
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  icon: {
    color: theme.palette.grey[700],
    height: 24,
    width: 24,
  },
  iconContainer: {
    alignItems: 'center',
    backgroundColor: theme.palette.grey[100],
    borderRadius: '50%',
    display: 'flex',
    height: 35,
    justifyContent: 'center',
    width: 35,
  },
  integrationIcons: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(5),
    width: '100%',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  title: {
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 600,
  },
}));

interface FeatureCard {
  description: string;
  icon: React.ReactNode;
  title: string;
  onClick?: () => void;
}

export interface ChatFeatureCardsProps {
  onAddContext?: () => void;
  onHistory?: () => void;
  onIntegrations?: () => void;
  onSavePrompt?: () => void;
}

export function ChatFeatureCards({
  onAddContext,
  onHistory,
  onIntegrations,
  onSavePrompt,
}: ChatFeatureCardsProps = {}) {
  const { classes } = useStyles();

  const features: FeatureCard[] = [
    {
      description: 'Add contracts, pleadings, or PDFs for quick analysis.',
      icon: (
        <div className={classes.iconContainer}>
          <CrossIcon style={{ height: 20, width: 20 }} />
        </div>
      ),
      onClick: onAddContext,
      title: 'Upload Legal Documents',
    },
    {
      description: 'Connect your integrations, select the files to sync, and start asking.',
      icon: (
        <div className={classes.iconContainer}>
          <IntegrationBlocksIcon style={{ height: 18, width: 18 }} />
        </div>
      ),
      onClick: onIntegrations,
      title: 'Integrations',
    },
    {
      description: 'Redline, compare, draft, and analyze documents with ready-to-use prompts.',
      icon: (
        <div className={classes.iconContainer}>
          <BookOpenIcon style={{ height: 18, width: 18 }} />
        </div>
      ),
      onClick: onSavePrompt,
      title: 'Aline Prompts',
    },
    {
      description: 'Revisit previous drafts, research, and document insights.',
      icon: (
        <div className={classes.iconContainer}>
          <RevisionHistoryIcon style={{ height: 24, width: 24 }} />
        </div>
      ),
      onClick: onHistory,
      title: 'Access Chat History',
    },
  ];

  return (
    <div className={classes.root}>
      <div className={classes.contentWrapper}>
        <Typography className={classes.sectionTitle} variant="h5">
          Explore Key Chat Features
        </Typography>
        <div className={classes.cardsContainer}>
          {features.map((feature, index) => (
            <Card key={index} className={classes.card} onClick={feature.onClick}>
              <div className={classes.cardContent}>
                {feature.icon}
                <Typography className={classes.title}>{feature.title}</Typography>
                <Typography className={classes.description}>{feature.description}</Typography>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
